/**
 * NaamDarbar — Theme Context
 *
 * Two themes:
 *   • "sukhasan" — Deep-blue celestial dark theme (default)
 *   • "prakash"  — Warm saffron/orange light theme
 */

import { createContext, useContext, useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';

export type ThemeName = 'prakash' | 'sukhasan';

interface ThemeContextValue {
  theme: ThemeName;
  toggleTheme: () => void;
  setTheme: (t: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'naamdarbar-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'prakash' || stored === 'sukhasan') return stored;
    }
    return 'sukhasan';
  });

  // Apply data-theme attribute on <html>
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);

    // Also toggle a class for Tailwind dark variant
    if (theme === 'sukhasan') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'prakash' ? 'sukhasan' : 'prakash'));
  }, []);

  const setTheme = useCallback((t: ThemeName) => {
    setThemeState(t);
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme, setTheme }), [theme, toggleTheme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
